import { useState, type FormEvent } from 'react';
import {
  Plus,
  Trash2,
  Star,
  Pencil,
  Receipt,
  FileCheck2,
  Wrench,
  KeyRound,
  Users,
  Mail,
  Phone,
  Copy,
} from 'lucide-react';
import { Button } from '@/components/button';
import { Card, CardBody } from '@/components/card';
import { DataList, DataListRow } from '@/components/data-list';
import { Dialog } from '@/components/dialog';
import { EntityAvatar } from '@/components/entity-avatar';
import { Input } from '@/components/input';
import {
  ChannelFields,
  PHONE_LABELS,
  EMAIL_LABELS,
  type ChannelRow,
} from '@/components/channel-fields';
import { Textarea } from '@/components/textarea';
import { Badge } from '@/components/badge';
import { EmptyState } from '@/components/empty-state';
import { Menu, type MenuItem } from '@/components/menu';
import { useToast } from '@/components/toast';
import { useConfirm } from '@/components/confirm';
import {
  useContacts,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
} from '@/lib/contacts';
import { useCreateInvitation } from '@/lib/invitations';
import { useFormError, extractMessage } from '@/lib/form-error';
import { formatPhone } from '@/lib/format';
import type { ContactData } from '@/types/api';

type PhoneInput = { label?: string; number: string; is_primary?: boolean };
type EmailInput = { label?: string; address: string; is_primary?: boolean };

type ContactInput = {
  name: string;
  title?: string;
  phones?: PhoneInput[];
  emails?: EmailInput[];
  is_primary?: boolean;
  receives_billing?: boolean;
  receives_approvals?: boolean;
  receives_technical?: boolean;
  notes?: string;
};


export function ClientContacts({ clientId }: { clientId: number }) {
  const { data, isLoading } = useContacts(clientId);
  const create = useCreateContact(clientId);
  const remove = useDeleteContact(clientId);
  const invite = useCreateInvitation();
  const confirm = useConfirm();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  async function inviteToPortal(contact: ContactData) {
    if (!contact.primary_email) {
      return;
    }
    const ok = await confirm({
      title: `Invite ${contact.name} to the portal?`,
      body: `We'll email ${contact.primary_email} a link to set a password. They start with no access — you choose what they can see under Portal access once they accept.`,
      confirmLabel: 'Send invitation',
    });
    if (!ok) {
      return;
    }
    try {
      await invite.mutateAsync({
        name: contact.name,
        email: contact.primary_email,
        role: 'client_user',
        client_id: clientId,
      });
      toast.success(`Portal invitation sent to ${contact.primary_email}`);
    } catch (err) {
      toast.error(extractMessage(err));
    }
  }

  const editing = editingId != null ? (data ?? []).find((c) => c.id === editingId) ?? null : null;
  const deleting = deletingId != null ? (data ?? []).find((c) => c.id === deletingId) ?? null : null;

  const primary = (data ?? []).find((c) => c.is_primary) ?? null;
  const others = (data ?? []).filter((c) => !c.is_primary);

  return (
    <div className="space-y-4">
      {data && data.length > 0 ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {data.length} {data.length === 1 ? 'contact' : 'contacts'}
            {primary ? ` · Primary: ${primary.name}` : ' · No primary set'}
          </p>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Add contact
          </Button>
        </div>
      ) : null}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No contacts yet"
          description="Add the people you work with at this client."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Add contact
            </Button>
          }
        />
      ) : (
        <div className="space-y-4">
          {primary ? (
            <PrimaryHero
              contact={primary}
              onEdit={() => setEditingId(primary.id)}
              onDelete={() => setDeletingId(primary.id)}
              onInvite={() => void inviteToPortal(primary)}
            />
          ) : null}

          {others.length > 0 ? (
            <DataList>
              {others.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  onEdit={() => setEditingId(c.id)}
                  onDelete={() => setDeletingId(c.id)}
                  onInvite={() => void inviteToPortal(c)}
                />
              ))}
            </DataList>
          ) : null}
        </div>
      )}

      <ContactDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add contact"
        submitLabel="Add"
        onSubmit={async (input) => {
          await create.mutateAsync(input);
          toast.success('Contact added');
          setCreateOpen(false);
        }}
      />

      {editing ? (
        <EditContactDialog
          contact={editing}
          clientId={clientId}
          onDone={() => setEditingId(null)}
        />
      ) : null}

      <Dialog
        open={deleting != null}
        onClose={() => setDeletingId(null)}
        title={deleting ? `Delete ${deleting.name}?` : ''}
      >
        <p className="text-sm text-muted-foreground">
          They'll be removed from this client. Any role flags they had (billing, approvals,
          technical) will need to be assigned to someone else.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setDeletingId(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={remove.isPending}
            onClick={async () => {
              if (!deleting) return;
              await remove.mutateAsync(deleting.id);
              toast.success(`${deleting.name} removed`);
              setDeletingId(null);
            }}
          >
            {remove.isPending ? 'Deleting…' : 'Delete contact'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function PrimaryHero({
  contact,
  onEdit,
  onDelete,
  onInvite,
}: {
  contact: ContactData;
  onEdit: () => void;
  onDelete: () => void;
  onInvite: () => void;
}) {
  const toast = useToast();
  function copy(value: string, label: string) {
    void navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  }
  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/[0.04] to-card">
      <CardBody className="flex items-start gap-4 p-5">
        <EntityAvatar name={contact.name} size="lg" />
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="truncate text-lg font-semibold tracking-tight">{contact.name}</h3>
                <Badge tone="info">
                  <Star className="mr-1 size-3 fill-current" /> Primary
                </Badge>
              </div>
              {contact.title ? (
                <p className="text-sm text-muted-foreground">{contact.title}</p>
              ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <Button variant="secondary" onClick={onEdit}>
                <Pencil className="size-3.5" /> Edit
              </Button>
              <Menu items={contactMenuItems(contact, onInvite, onDelete)} />
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-sm">
            {contact.emails.map((e) => (
              <CopyableLink
                key={e.id}
                icon={Mail}
                href={`mailto:${e.address}`}
                label={e.address}
                badge={e.label}
                onCopy={() => copy(e.address, 'Email')}
              />
            ))}
            {contact.phones.map((p) => (
              <CopyableLink
                key={p.id}
                icon={Phone}
                href={`tel:${p.number.replace(/\D/g, '')}`}
                label={formatPhone(p.number)}
                badge={p.label}
                onCopy={() => copy(p.number, 'Phone')}
              />
            ))}
          </div>

          <RoleBadges contact={contact} />

          {contact.notes ? (
            <p className="whitespace-pre-wrap pt-1 text-sm text-foreground">{contact.notes}</p>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}

function CopyableLink({
  icon: Icon,
  href,
  label,
  badge,
  onCopy,
}: {
  icon: typeof Mail;
  href: string;
  label: string;
  badge?: string;
  onCopy: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md bg-muted/50 px-2 py-1 text-sm">
      <Icon className="size-3.5 text-muted-foreground" />
      <a href={href} className="text-foreground hover:underline">
        {label}
      </a>
      {badge ? <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{badge}</span> : null}
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy"
        className="text-muted-foreground hover:text-foreground"
      >
        <Copy className="size-3" />
      </button>
    </span>
  );
}

function ContactRow({
  contact,
  onEdit,
  onDelete,
  onInvite,
}: {
  contact: ContactData;
  onEdit: () => void;
  onDelete: () => void;
  onInvite: () => void;
}) {
  const toast = useToast();
  function copy(value: string, label: string) {
    void navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  }
  return (
    <DataListRow interactive={false}>
      <EntityAvatar name={contact.name} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{contact.name}</span>
          {contact.title ? (
            <span className="truncate text-xs text-muted-foreground">· {contact.title}</span>
          ) : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          {contact.emails.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => copy(e.address, 'Email')}
              className="inline-flex items-center gap-1 hover:text-foreground"
              title="Copy email"
            >
              <Mail className="size-3" />
              <span className="truncate">{e.address}</span>
            </button>
          ))}
          {contact.phones.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => copy(p.number, 'Phone')}
              className="inline-flex items-center gap-1 hover:text-foreground"
              title="Copy phone"
            >
              <Phone className="size-3" />
              <span>{formatPhone(p.number)}</span>
            </button>
          ))}
          <RoleBadges contact={contact} inline />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Button variant="secondary" onClick={onEdit}>
          <Pencil className="size-3.5" /> Edit
        </Button>
        <Menu items={contactMenuItems(contact, onInvite, onDelete)} />
      </div>
    </DataListRow>
  );
}

/**
 * Shared row/hero overflow-menu items. "Invite to portal" only appears when
 * the contact has an email and doesn't already have portal access.
 */
function contactMenuItems(
  contact: ContactData,
  onInvite: () => void,
  onDelete: () => void,
): MenuItem[] {
  return [
    ...(contact.primary_email && !contact.has_portal_access
      ? [{ key: 'invite', label: 'Invite to portal', icon: KeyRound, onSelect: onInvite }]
      : []),
    { key: 'delete', label: 'Delete', icon: Trash2, danger: true, onSelect: onDelete },
  ];
}

function EditContactDialog({
  contact,
  clientId,
  onDone,
}: {
  contact: ContactData;
  clientId: number;
  onDone: () => void;
}) {
  const update = useUpdateContact(clientId, contact.id);
  return (
    <ContactDialog
      open={true}
      onClose={onDone}
      title="Edit contact"
      submitLabel="Save"
      initial={contact}
      onSubmit={async (input) => {
        await update.mutateAsync(input);
        onDone();
      }}
    />
  );
}

function RoleBadges({ contact, inline = false }: { contact: ContactData; inline?: boolean }) {
  const badges: Array<{ key: string; label: string; icon: typeof Star; tone: 'info' | 'success' | 'warning' | 'neutral' }> = [];
  if (contact.receives_billing) badges.push({ key: 'billing', label: 'Billing', icon: Receipt, tone: 'success' });
  if (contact.receives_approvals) badges.push({ key: 'approvals', label: 'Approvals', icon: FileCheck2, tone: 'warning' });
  if (contact.receives_technical) badges.push({ key: 'technical', label: 'Technical', icon: Wrench, tone: 'neutral' });
  if (contact.has_portal_access) badges.push({ key: 'portal', label: 'Portal', icon: KeyRound, tone: 'info' });

  if (badges.length === 0) return null;
  return (
    <div className={inline ? 'inline-flex flex-wrap gap-1' : 'flex flex-wrap gap-1'}>
      {badges.map((b) => (
        <Badge key={b.key} tone={b.tone}>
          <b.icon className="mr-1 size-3" />
          {b.label}
        </Badge>
      ))}
    </div>
  );
}

function ContactDialog({
  open,
  onClose,
  onSubmit,
  title,
  submitLabel,
  initial,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: ContactInput) => Promise<void>;
  title: string;
  submitLabel: string;
  initial?: ContactData;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [titleVal, setTitle] = useState(initial?.title ?? '');
  const [phones, setPhones] = useState<ChannelRow[]>(
    initial?.phones.map((p) => ({ label: p.label, value: p.number, is_primary: p.is_primary })) ??
      [],
  );
  const [emails, setEmails] = useState<ChannelRow[]>(
    initial?.emails.map((e) => ({ label: e.label, value: e.address, is_primary: e.is_primary })) ??
      [],
  );
  const [isPrimary, setIsPrimary] = useState(initial?.is_primary ?? false);
  const [billing, setBilling] = useState(initial?.receives_billing ?? false);
  const [approvals, setApprovals] = useState(initial?.receives_approvals ?? false);
  const [technical, setTechnical] = useState(initial?.receives_technical ?? false);
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [pending, setPending] = useState(false);
  const { error, wrap } = useFormError();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPending(true);
    const ok = await wrap(() =>
      onSubmit({
        name,
        title: titleVal || undefined,
        phones: phones
          .filter((p) => p.value.trim())
          .map((p) => ({ label: p.label, number: p.value.trim(), is_primary: p.is_primary })),
        emails: emails
          .filter((e) => e.value.trim())
          .map((e) => ({ label: e.label, address: e.value.trim(), is_primary: e.is_primary })),
        is_primary: isPrimary,
        receives_billing: billing,
        receives_approvals: approvals,
        receives_technical: technical,
        notes: notes || undefined,
      }),
    );
    setPending(false);
    if (ok && !initial) {
      setName('');
      setTitle('');
      setPhones([]);
      setEmails([]);
      setIsPrimary(false);
      setBilling(false);
      setApprovals(false);
      setTechnical(false);
      setNotes('');
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <form className="space-y-3" onSubmit={handleSubmit}>
        <Input required placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
        <Input placeholder="Title" value={titleVal} onChange={(e) => setTitle(e.target.value)} />

        <ChannelFields
          heading="Phone numbers"
          type="phone"
          rows={phones}
          setRows={setPhones}
          labels={PHONE_LABELS}
        />
        <ChannelFields
          heading="Email addresses"
          type="email"
          rows={emails}
          setRows={setEmails}
          labels={EMAIL_LABELS}
        />

        <fieldset className="rounded-md border border-border p-3">
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Receives
          </legend>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <RoleCheckbox
              label="Primary contact"
              icon={Star}
              checked={isPrimary}
              onChange={setIsPrimary}
            />
            <RoleCheckbox
              label="Billing / invoices"
              icon={Receipt}
              checked={billing}
              onChange={setBilling}
            />
            <RoleCheckbox
              label="Proposals / approvals"
              icon={FileCheck2}
              checked={approvals}
              onChange={setApprovals}
            />
            <RoleCheckbox
              label="Technical alerts"
              icon={Wrench}
              checked={technical}
              onChange={setTechnical}
            />
          </div>
        </fieldset>

        <Textarea
          placeholder="Notes (optional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <div className="flex justify-end pt-2">
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RoleCheckbox({
  label,
  icon: Icon,
  checked,
  onChange,
}: {
  label: string;
  icon: typeof Star;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <Icon className="size-3.5 text-muted-foreground" />
      <span>{label}</span>
    </label>
  );
}
